import { useEffect, useMemo, useRef, useState } from "react"

import debounce from "lodash/debounce"

import { EventEmitter } from "@treefort/lib/event-emitter"

/**
 * This hook makes it easy to get the latest value from an event emitter. It
 * takes in the event emitter, the event to listen to, and a getter function to
 * provide the initial value. This hook ensures that no values are missed
 * between fetching the initial value and adding the event listener.
 */
export function useEventEmitterValue<
  EventMap,
  Event extends keyof EventMap,
  ReturnValue,
>(
  eventEmitter: EventEmitter<EventMap>,
  event: Event,
  getValue: () => ReturnValue,
  debounceMs?: number,
): EventMap[Event] | ReturnValue {
  const initialValue = useRef(getValue())
  const [value, setValue] = useState<EventMap[Event] | ReturnValue>(
    initialValue.current,
  )
  const setValueDebounced = useMemo(
    () =>
      debounceMs
        ? debounce(setValue, debounceMs, { leading: true, trailing: true })
        : setValue,
    [debounceMs],
  )
  useEffect(() => {
    const removeListener = eventEmitter.on(event, setValueDebounced)

    // Update our local state if we missed a value between the initial render
    // and binding the event listener
    const currentValue = getValue()
    if (initialValue.current !== currentValue) {
      setValueDebounced(currentValue)
    }

    return removeListener
  }, [setValueDebounced, getValue, eventEmitter, event])
  return value
}
